/**
 * Chakra 5 — Vishuddha (§10). Element Ether, kleur blauw, mantra HAM (741Hz).
 * Resource-label: Stemkracht. Wereld: "De Gesmoorde Stem".
 */
import type { Chakra } from './types';
import { HEADERS } from './headers';

export const chakra5: Chakra = {
  header: HEADERS[5],
  wereld: 'De Gesmoorde Stem',
  codex:
    'Vishuddha, "zeer zuiver", zit bij de keel. Element Ether, kleur blauw, 16 bloemblaadjes, mantra HAM (741Hz). Levensfase: schoolleeftijd/vroege puberteit, eigen mening durven vormen. Levensvraag: mag ik mijn waarheid spreken? Lichamelijk: keel, schildklier, stembanden, nek, kaak. Tekort: zwijgzaam, bang voor afwijzing bij het uiten van je mening, je passen aan anderen aan, liegen tegen jezelf. Teveel: dominant in het woord, te veel praten zonder te luisteren, roddelen, beloften niet nakomen. Balans: heldere, authentieke communicatie, goed luisteren én duidelijk spreken, je waarheid kennen. Herstel: zingen, journaling, hardop spreken, bewust luisteren, stilte oefenen.',

  kaarten: [
    { id: 'ch5-aquamarijn', naam: 'Aquamarijn', type: 's', cost: 1, origin: 5, tekst: '+3 stemkracht, neutraliseert zwijgaanvallen', effect: { block: 3, cleanse: 'one' } },
    { id: 'ch5-lapis-lazuli', naam: 'Lapis Lazuli', type: 's', cost: 2, origin: 5, tekst: 'Authentieke kaarten +2 effect deze beurt', effect: { buffNext: 2 } },
    { id: 'ch5-blauwe-calciet', naam: 'Blauwe Calciet', type: 's', cost: 1, origin: 5, tekst: '+2 energie, keel ontspant', effect: { energy: 2 } },
    { id: 'ch5-sodaliet', naam: 'Sodaliet', type: 's', cost: 2, origin: 5, tekst: 'Mantrakaarten -1 kosten (3 beurten)', effect: { costDownType: { type: 'm', amt: 1, turns: 3 } } },
    { id: 'ch5-celestien', naam: 'Celestien', type: 's', cost: 3, origin: 5, tekst: 'Alle keelkaarten ×2', effect: { mult: { origin: 5, amt: 2 } } },
    { id: 'ch5-schildklier-balans', naam: 'Schildklier Balans', type: 'o', cost: 2, origin: 5, tekst: 'Neutraliseert energieblokkades', effect: { cleanse: 'one' } },
    { id: 'ch5-keel-activatie', naam: 'Keel Activatie', type: 'o', cost: 1, origin: 5, tekst: '+2 energie, mantrakaarten +1', effect: { energy: 2, buffNext: 1 } },
    { id: 'ch5-nek-vrijmaken', naam: 'Nek Vrijmaken', type: 'o', cost: 1, origin: 5, tekst: 'Verwijder 1 expressieblokkade', effect: { cleanse: 'one' } },
    { id: 'ch5-tong-kracht', naam: 'Tong Kracht', type: 'o', cost: 2, origin: 5, tekst: 'Volgende 3 kaarten kosten 0', effect: { freeNextCount: 3 } },
    { id: 'ch5-zingen', naam: 'Zingen', type: 'p', cost: 2, origin: 5, tekst: '+5 schade aan baas, bevrijdt stem', effect: { dmg: 5, cleanse: 'one' } },
    { id: 'ch5-journaling', naam: 'Journaling', type: 'p', cost: 1, origin: 5, tekst: 'Trek 3 kaarten, verwerk blokkade', effect: { draw: 3, cleanse: 'one' } },
    { id: 'ch5-waarheid-spreken', naam: 'Waarheid Spreken', type: 'p', cost: 3, origin: 5, tekst: 'Baas -8 HP, alle blokkades weg', effect: { dmg: 8, cleanse: 'all' } },
    { id: 'ch5-spreken-voor-publiek', naam: 'Spreken voor Publiek', type: 'p', cost: 2, origin: 5, tekst: 'Aanvallen +2 (2 beurten)', effect: { buffOutput: { amt: 2, turns: 2 } } },
    { id: 'ch5-stilte', naam: 'Stilte', type: 'p', cost: 1, origin: 5, tekst: 'Baas slaat 1 aanval over', effect: { bossSkip: true } },
    { id: 'ch5-ether-element', naam: 'Ether Element', type: 'e', cost: 3, origin: 5, tekst: 'Alle Vishuddha-kaarten ×2', effect: { mult: { origin: 5, amt: 2 } } },
    { id: 'ch5-zuivere-stem', naam: 'Zuivere Stem', type: 'e', cost: 2, origin: 5, tekst: 'Alle stemblokkades weg', effect: { cleanse: 'all' }, meesterschap: true },
    { id: 'ch5-ham-mantra', naam: 'HAM Mantra', type: 'm', cost: 2, origin: 5, tekst: 'Doorbreekt stemblokkade, +3 stemkracht', effect: { cleanse: 'one', block: 3, tone: 741 } },
    { id: 'ch5-ham-verdiept', naam: 'HAM Verdiept', type: 'm', cost: 3, origin: 5, tekst: 'Alle blokkades weg, +6 HP', effect: { cleanse: 'all', heal: 6, tone: 741 } },
    { id: 'ch5-blauw-visualisatie', naam: 'Blauw Visualisatie', type: 'k', cost: 1, origin: 5, tekst: '+2 energie, expressiekaarten +1', effect: { energy: 2, buffNext: 1 } },
    { id: 'ch5-blauwe-ademhaling', naam: 'Blauwe Ademhaling', type: 'k', cost: 2, origin: 5, tekst: 'Trek 2 kaarten, verwijder 1 blokkade', effect: { draw: 2, cleanse: 'one' } },
  ],

  bazen: [
    {
      id: 'ch5-tharven',
      naam: 'Tharven',
      titel: 'De Zwijgoplegger',
      hp: 26,
      energie: 2,
      aanvallen: [
        { naam: 'Zwijggebod', cost: 2, effect: { status: { kind: 'costUp', type: 'm', amt: 3, turns: 2 } } },
        { naam: 'Woorden Inslikken', cost: 2, effect: { dmg: 3, energyLoss: 1 } },
        { naam: 'Stem Wegnemen', cost: 3, effect: { status: { kind: 'lockType', type: 'p', turns: 1 } } },
      ],
      zwaktes: [
        { card: 'Aquamarijn', counters: 'Zwijggebod' },
        { card: 'Zingen', bonusDmg: 5 },
        { card: 'Blauw Visualisatie', counters: 'Stem Wegnemen' },
      ],
    },
    {
      id: 'ch5-falsek',
      naam: 'Falsek',
      titel: 'De Maskermaker',
      hp: 32,
      energie: 3,
      aanvallen: [
        { naam: 'Masker Opzetten', cost: 2, effect: { status: { kind: 'halfEffectType', type: 'p', turns: 1 } } },
        { naam: 'Aanpassen', cost: 2, effect: { forcePlayRandom: true } },
        { naam: 'Valse Stem', cost: 3, effect: { status: { kind: 'lockType', type: 'm', turns: 1 } } },
      ],
      zwaktes: [
        { card: 'Lapis Lazuli', counters: 'Masker Opzetten', bonusBuff: 2 },
        { card: 'Waarheid Spreken', bonusDmg: 8 },
        { card: 'HAM Mantra', counters: 'Valse Stem' },
      ],
    },
    {
      id: 'ch5-drumath',
      naam: 'Drumath',
      titel: 'De Overdonderende',
      hp: 30,
      energie: 3,
      aanvallen: [
        { naam: 'Overdonderend Lawaai', cost: 2, effect: { status: { kind: 'maxPlay', amt: 1, turns: 1 } } },
        { naam: 'Alles Overstemmen', cost: 3, effect: { status: { kind: 'nullifyAll', turns: 1 } } },
        { naam: 'Uitputting door Lawaai', cost: 2, effect: { energyLoss: 3 } },
      ],
      zwaktes: [
        { card: 'Ether Element', counters: 'Overdonderend Lawaai' },
        { card: 'Stilte', counters: '*' },
        { cards: ['Aquamarijn', 'HAM Mantra'], bonusDmg: 10, sameTurn: true },
      ],
    },
    {
      id: 'ch5-silvareth',
      naam: 'Silvareth',
      titel: 'De Gesmoorde Waarheid',
      hp: 65,
      energie: 4,
      eindbaas: true,
      passief: 'Speelt de speler geen Praktijk-kaart in een beurt → Silvareth +3 HP.',
      aanvallen: [
        {
          naam: 'Totaal Zwijgen',
          cost: 4,
          effect: {
            status: [
              { kind: 'lockType', type: 'p', turns: 2 },
              { kind: 'lockType', type: 'm', turns: 2 },
            ],
          },
        },
        { naam: 'Steen in de Keel', cost: 3, effect: { dmg: 8 } },
        { naam: 'Identiteitsverlies', cost: 3, effect: { discard: { n: 2, mode: 'strongest' } } },
        { naam: 'Echo Kamer', cost: 2, effect: { status: { kind: 'selfDamageShare', amt: 0.5, turns: 1 } } },
      ],
      combo: { bossHpLoss: 25 },
      zwaktes: [
        { cards: ['HAM Mantra', 'Lapis Lazuli'], counters: 'Totaal Zwijgen', sameTurn: true },
        { cards: ['Waarheid Spreken', 'Zingen'], counters: 'Echo Kamer', reflectBonus: 6, sameTurn: true },
      ],
      unlock: 'ch5-zuivere-stem',
      leermoment:
        'Silvareth toont wat onuitgesproken waarheid doet: ze verdwijnt niet, ze verzwaart. Elke leugen aan jezelf is een steen erbij. Jouw stem is geen risico — het is je weg terug naar jezelf. Herstel: hardop spreken wat je voelt, ook als je stem trilt, en luisteren zonder jezelf te verliezen.',
    },
  ],
};
